import { StrictMode, Suspense, lazy, useEffect, useRef, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./theme.css";
import Preview from "./pages/Preview";
import { ToastHost } from "./ui/Toast";
import { registerServiceWorker } from "./pwa";
import { settleAfterKeyboard } from "./keyboard";
import { LAST_FILTER, recall } from "./session";
import {
  AskShell,
  FormShell,
  LeadShell,
  ListShell,
  NewsletterShell,
  PreviewShell,
  ReviewsShell,
  StatsShell,
} from "./ui/Shell";
import { SeoShell } from "./seo/Shell";

/* The studio is loaded on demand. A client opening a preview link should not
   download an admin console to look at a picture of their dog. */
const loadRequireAdmin = () => import("./pages/RequireAdmin");
const loadStudioList = () => import("./pages/StudioList");
const loadStudioLead = () => import("./pages/StudioLead");
const loadStudioNew = () => import("./pages/StudioNew");
const loadStudioStats = () => import("./pages/StudioStats");
const loadStudioAsk = () => import("./pages/StudioAsk");
const loadStudioAskCheck = () => import("./pages/StudioAskCheck");
const loadStudioSeo = () => import("./pages/StudioSeo");
const loadStudioNewsletter = () => import("./pages/StudioNewsletter");
const loadStudioReviews = () => import("./pages/StudioReviews");

const RequireAdmin = lazy(loadRequireAdmin);
const StudioList = lazy(loadStudioList);
const StudioLead = lazy(loadStudioLead);
const StudioNew = lazy(loadStudioNew);
const StudioStats = lazy(loadStudioStats);
const StudioAsk = lazy(loadStudioAsk);
const StudioAskCheck = lazy(loadStudioAskCheck);
const StudioSeo = lazy(loadStudioSeo);
const StudioNewsletter = lazy(loadStudioNewsletter);
const StudioReviews = lazy(loadStudioReviews);

/* Their piece has arrived. A page of its own, asked for only by the arrival
   mail, so it stays out of the bundle every preview link downloads. */
const Arrived = lazy(() => import("./pages/Arrived"));

/** Ask for the studio's chunks now, rather than finding out we need them one
 *  at a time.
 *
 *  React only reaches for a lazy chunk at the moment it tries to render it,
 *  and the three it needs to draw a lead are nested inside one another: the
 *  gate, then everything Firebase, then the screen. Left alone that is four
 *  round trips end to end — the bundle, the gate, the sign-in check, the
 *  screen — with nothing but the last one's answer ever on the screen. On a
 *  phone on cellular it is most of the wait.
 *
 *  A dynamic import is cached by the module graph, so asking here costs
 *  nothing later: by the time React wants them they are downloaded, or already
 *  on their way. Only on the studio's own paths — the client's page must not
 *  pull half a megabyte of admin console behind it. */
function warmStudio(path: string): void {
  if (path.startsWith("/p/")) return;
  void loadRequireAdmin();
  if (path === "/studio/new") void loadStudioNew();
  else if (path === "/studio/stats") void loadStudioStats();
  else if (path === "/studio/ask") void loadStudioAsk();
  else if (path === "/studio/ask/check") void loadStudioAskCheck();
  else if (path === "/studio/seo") void loadStudioSeo();
  else if (path === "/studio/newsletter") void loadStudioNewsletter();
  else if (path === "/studio/reviews") void loadStudioReviews();
  else if (/^\/studio\/[^/]+$/.test(path)) void loadStudioLead();
  else void loadStudioList();

  /* And then the other one. From the list the next thing that happens is a
     lead being tapped, and from a lead it is the way back — both of them a tap
     with nothing to cover it. Asked for only once the launch has gone quiet,
     so it competes with the screen somebody is actually waiting for. */
  const rest = () => {
    void loadStudioList();
    void loadStudioLead();
  };
  // Safari only learned requestIdleCallback in 16.4, and this app is opened on
  // phones older than that.
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(rest, { timeout: 4000 });
  } else {
    window.setTimeout(rest, 2000);
  }
}
warmStudio(window.location.pathname);

/** The gate, and the two waits either side of it, all wearing the same face.
 *
 *  There are three separate things to wait for before a studio screen can be
 *  drawn — its gate, the answer about who is signed in, and the screen itself
 *  — and a client on the train hits all three in a row. Handing each of them
 *  the same shell means the screen is assembled once and then filled in, not
 *  rebuilt three times. */
function Studio({ shell, children }: { shell: ReactNode; children: ReactNode }) {
  return (
    <Suspense fallback={shell}>
      <RequireAdmin fallback={shell}>
        <Suspense fallback={shell}>{children}</Suspense>
      </RequireAdmin>
    </Suspense>
  );
}

/** The screen a launch lands on was drawn by index.html before React existed,
 *  so it does not get the arrival animation — it has already arrived. Every
 *  screen after it is one you went to, and those do. */
function Boot() {
  const { pathname } = useLocation();
  const landed = useRef(pathname);

  useEffect(() => {
    if (pathname !== landed.current) document.documentElement.removeAttribute("data-booting");
  }, [pathname]);

  return null;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <Boot />
      <Routes>
        {/* The client's whole world: one link, no account. */}
        <Route path="/p/:token" element={<Preview />} />
        <Route
          path="/p/:token/arrived"
          element={
            <Suspense fallback={<PreviewShell />}>
              <Arrived />
            </Suspense>
          }
        />
        <Route
          path="/studio"
          element={
            <Studio shell={<ListShell filter={lastFilter()} />}>
              <StudioList />
            </Studio>
          }
        />
        <Route
          path="/studio/new"
          element={
            <Studio shell={<FormShell />}>
              <StudioNew />
            </Studio>
          }
        />
        <Route
          path="/studio/stats"
          element={
            <Studio shell={<StatsShell />}>
              <StudioStats />
            </Studio>
          }
        />
        <Route
          path="/studio/seo"
          element={
            <Studio shell={<SeoShell />}>
              <StudioSeo />
            </Studio>
          }
        />
        <Route
          path="/studio/newsletter"
          element={
            <Studio shell={<NewsletterShell />}>
              <StudioNewsletter />
            </Studio>
          }
        />
        <Route
          path="/studio/reviews"
          element={
            <Studio shell={<ReviewsShell />}>
              <StudioReviews />
            </Studio>
          }
        />
        <Route
          path="/studio/ask"
          element={
            <Studio shell={<AskShell />}>
              <StudioAsk />
            </Studio>
          }
        />
        <Route
          path="/studio/ask/check"
          element={
            <Studio shell={<AskShell />}>
              <StudioAskCheck />
            </Studio>
          }
        />
        <Route
          path="/studio/:leadId"
          element={
            <Studio shell={<LeadShell />}>
              <StudioLead />
            </Studio>
          }
        />
        <Route path="*" element={<Navigate to="/studio" replace />} />
      </Routes>
      <ToastHost />
    </BrowserRouter>
  </StrictMode>
);

/** Which chip to draw lit, before the list that owns them exists. The same
 *  default the list uses: a wrong guess would light one chip and then light
 *  another, which is the one thing a shell must not do. */
function lastFilter(): string {
  return recall(LAST_FILTER) ?? "open";
}

registerServiceWorker();
settleAfterKeyboard();
